import {
  Button,
  Card,
  Dialog,
  EmptyState,
  Field,
  Input,
  Select,
  SkeletonText,
  Switch,
  useConfirm,
  useToast,
} from '@aurora/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, PieChart, Plus, Save, Share2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';

import { api } from '../api/client.js';
import { tenantStore } from '../state/tenant.js';

/**
 * v5/F1: Auswertungen — Self-Service-Report-Builder. Quellen und Felder
 * liefern die Module über die ANALYTICS-Registry; die Query-Engine im Host
 * führt aus. Hier: Quelle wählen → Kennzahlen/Gruppierung/Filter → Vorschau
 * (Tabelle/Balken/Linien) → Drilldown auf Datensätze → speichern/exportieren.
 */

type FieldType = 'text' | 'number' | 'money' | 'date' | 'boolean';
type Aggregate = 'count' | 'sum' | 'avg' | 'min' | 'max';
type Bucket = 'day' | 'week' | 'month' | 'quarter' | 'year';
type FilterOp = 'eq' | 'neq' | 'contains' | 'gte' | 'lte' | 'between' | 'empty' | 'not_empty';
type ChartKind = 'table' | 'bars' | 'lines';

interface SourceField {
  key: string;
  label: string;
  type: FieldType;
  options?: { value: string; label: string }[];
  groupable: boolean;
  aggregatable: boolean;
  filterable: boolean;
}
interface Source {
  key: string;
  label: string;
  moduleLabel: string;
  fields: SourceField[];
  defaultColumns: string[];
  detail?: { route: string };
}
interface MetricDef {
  aggregate: Aggregate;
  field?: string;
}
interface GroupByDef {
  field: string;
  bucket?: Bucket;
}
/** Filterzustand der UI — Werte als Strings, Umrechnung erst beim Abschicken. */
interface FilterDraft {
  field: string;
  op: FilterOp;
  value: string;
  value2: string;
}
interface ResultColumn {
  key: string;
  label: string;
  type: FieldType;
  dimension: boolean;
  bucket?: Bucket;
  sourceField?: string;
}
interface QueryResult {
  columns: ResultColumn[];
  rows: Record<string, unknown>[];
  truncated: boolean;
}
interface SavedReport {
  id: string;
  name: string;
  description: string | null;
  config: Record<string, unknown>;
  shared: boolean;
  own: boolean;
}

const AGGREGATE_LABELS: Record<Aggregate, string> = {
  count: 'Anzahl',
  sum: 'Summe',
  avg: 'Durchschnitt',
  min: 'Minimum',
  max: 'Maximum',
};
const BUCKET_LABELS: Record<Bucket, string> = {
  day: 'Tag',
  week: 'Woche',
  month: 'Monat',
  quarter: 'Quartal',
  year: 'Jahr',
};
const OP_LABELS: Record<FilterOp, string> = {
  eq: 'ist',
  neq: 'ist nicht',
  contains: 'enthält',
  gte: 'ab',
  lte: 'bis',
  between: 'zwischen',
  empty: 'ist leer',
  not_empty: 'ist nicht leer',
};

function opsForType(type: FieldType, hasOptions: boolean): FilterOp[] {
  switch (type) {
    case 'text':
      return hasOptions
        ? ['eq', 'neq', 'empty', 'not_empty']
        : ['contains', 'eq', 'neq', 'empty', 'not_empty'];
    case 'date':
      return ['between', 'gte', 'lte', 'empty', 'not_empty'];
    case 'money':
    case 'number':
      return ['gte', 'lte', 'between', 'eq'];
    case 'boolean':
      return ['eq'];
  }
}

/** UI-Wert → Wire-Wert (Money in Cents, Zahlen als number, Rest string). */
function wireValue(field: SourceField, raw: string): string | number | boolean {
  if (field.type === 'money') return Math.round(Number(raw.replace(',', '.')) * 100);
  if (field.type === 'number') return Number(raw.replace(',', '.'));
  if (field.type === 'boolean') return raw === 'true';
  return raw;
}

function toWireFilters(source: Source, drafts: FilterDraft[]): Record<string, unknown>[] {
  const wire: Record<string, unknown>[] = [];
  for (const draft of drafts) {
    const field = source.fields.find((f) => f.key === draft.field);
    if (!field) continue;
    if (draft.op === 'empty' || draft.op === 'not_empty') {
      wire.push({ field: draft.field, op: draft.op });
      continue;
    }
    if (draft.value === '') continue;
    if (draft.op === 'between') {
      if (draft.value2 === '') continue;
      wire.push({
        field: draft.field,
        op: 'between',
        values: [wireValue(field, draft.value), wireValue(field, draft.value2)],
      });
      continue;
    }
    wire.push({ field: draft.field, op: draft.op, value: wireValue(field, draft.value) });
  }
  return wire;
}

function formatMoney(cents: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

function formatBucketValue(iso: string, bucket: Bucket): string {
  const [y, m, d] = iso.split('-') as [string, string, string];
  switch (bucket) {
    case 'day':
      return `${d}.${m}.${y}`;
    case 'week':
      return `Wo. ab ${d}.${m}.${y}`;
    case 'month':
      return `${m}/${y}`;
    case 'quarter':
      return `Q${Math.floor((Number(m) - 1) / 3) + 1} ${y}`;
    case 'year':
      return y;
  }
}

/** Zeitfenster eines Buckets (inklusives Ende) — fürs Drilldown-Filter. */
function bucketRange(iso: string, bucket: Bucket): [string, string] {
  const start = new Date(`${iso}T00:00:00Z`);
  const end = new Date(start);
  if (bucket === 'day') {
    // Start = Ende
  } else if (bucket === 'week') {
    end.setUTCDate(end.getUTCDate() + 6);
  } else if (bucket === 'month') {
    end.setUTCMonth(end.getUTCMonth() + 1);
    end.setUTCDate(end.getUTCDate() - 1);
  } else if (bucket === 'quarter') {
    end.setUTCMonth(end.getUTCMonth() + 3);
    end.setUTCDate(end.getUTCDate() - 1);
  } else {
    end.setUTCFullYear(end.getUTCFullYear() + 1);
    end.setUTCDate(end.getUTCDate() - 1);
  }
  return [iso, end.toISOString().slice(0, 10)];
}

/** Die Engine liefert Text/Datum als String — hier defensiv normalisiert. */
function cellText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value) ?? '';
}

function formatCell(column: ResultColumn, value: unknown, source: Source | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  if (column.bucket !== undefined) return formatBucketValue(cellText(value), column.bucket);
  if (column.type === 'money') return formatMoney(Number(value));
  if (column.type === 'number') return Number(value).toLocaleString('de-DE');
  if (column.type === 'date') {
    const text = cellText(value);
    const [y, m, d] = text.split('-');
    return d !== undefined ? `${d}.${m}.${y}` : text;
  }
  if (column.type === 'boolean') return value === true ? 'ja' : 'nein';
  // Options-Label auflösen (z. B. Status-Keys → deutsche Labels)
  const field = source?.fields.find((f) => f.key === column.sourceField);
  const option = field?.options?.find((o) => o.value === value);
  return option?.label ?? cellText(value);
}

/** Balken: erste Kennzahl je Gruppe — reines CSS (report-bar-Klassen von /berichte). */
function BarsPreview({ result, source }: { result: QueryResult; source: Source | undefined }) {
  const dims = result.columns.filter((c) => c.dimension);
  const metric = result.columns.find((c) => !c.dimension);
  if (!metric) return null;
  const max = Math.max(1, ...result.rows.map((r) => Math.abs(Number(r[metric.key] ?? 0))));
  return (
    <ul className="report-bars">
      {result.rows.slice(0, 30).map((row, index) => {
        const label = dims.map((d) => formatCell(d, row[d.key], source)).join(' · ') || '∑';
        const value = Number(row[metric.key] ?? 0);
        return (
          <li key={index} className="report-bar">
            <span className="report-bar__label" title={label}>
              {label}
            </span>
            <span className="report-bar__track">
              <span
                className="report-bar__fill"
                style={{
                  width: `${Math.max(2, (Math.abs(value) / max) * 100)}%`,
                  background: value < 0 ? '#dc2626' : 'var(--au-accent)',
                }}
              />
            </span>
            <span className="report-bar__value">{formatCell(metric, value, source)}</span>
          </li>
        );
      })}
    </ul>
  );
}

/** Linien: nur sinnvoll bei Zeit-Bucket als erster Gruppierung. */
function LinesPreview({ result, source }: { result: QueryResult; source: Source | undefined }) {
  const dim = result.columns.find((c) => c.dimension && c.bucket !== undefined);
  const metric = result.columns.find((c) => !c.dimension);
  if (!dim || !metric) return null;
  const rows = [...result.rows].sort((a, b) =>
    String(a[dim.key]).localeCompare(String(b[dim.key])),
  );
  const values = rows.map((r) => Number(r[metric.key] ?? 0));
  const min = Math.min(0, ...values);
  const max = Math.max(1, ...values);
  const width = 640;
  const height = 180;
  const x = (index: number): number =>
    rows.length <= 1 ? width / 2 : (index / (rows.length - 1)) * (width - 16) + 8;
  const y = (value: number): number =>
    height - 12 - ((value - min) / (max - min || 1)) * (height - 28);
  const points = values.map((v, i) => `${x(i)},${y(v)}`).join(' ');
  return (
    <div className="anl-lines">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={metric.label}>
        {min < 0 ? (
          <line x1="0" x2={width} y1={y(0)} y2={y(0)} className="ctl-lines__zero" />
        ) : null}
        <polyline points={points} fill="none" stroke="var(--au-accent)" strokeWidth="2" />
        {values.map((v, i) => (
          <circle key={i} cx={x(i)} cy={y(v)} r="2.5" fill="var(--au-accent)" />
        ))}
      </svg>
      <div className="ctl-lines__labels">
        <span>{formatCell(dim, rows[0]?.[dim.key], source)}</span>
        <span>{formatCell(dim, rows[rows.length - 1]?.[dim.key], source)}</span>
      </div>
    </div>
  );
}

export default function AnalyticsPage() {
  const toast = useToast();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const sources = useQuery({
    queryKey: ['analytics', 'sources'],
    queryFn: () => api<{ sources: Source[] }>('/api/v1/analytics/sources'),
  });
  const saved = useQuery({
    queryKey: ['analytics', 'saved'],
    queryFn: () => api<{ items: SavedReport[] }>('/api/v1/analytics/reports'),
  });

  const [sourceKey, setSourceKey] = useState('');
  const [metrics, setMetrics] = useState<MetricDef[]>([{ aggregate: 'count' }]);
  const [groupBy, setGroupBy] = useState<GroupByDef[]>([]);
  const [filters, setFilters] = useState<FilterDraft[]>([]);
  const [chartKind, setChartKind] = useState<ChartKind>('table');
  const [drill, setDrill] = useState<Record<string, unknown>[] | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveShared, setSaveShared] = useState(false);
  const [activeReport, setActiveReport] = useState<string | null>(null);

  const source = sources.data?.sources.find((s) => s.key === sourceKey);
  const groupableFields = source?.fields.filter((f) => f.groupable) ?? [];
  const aggregatableFields = source?.fields.filter((f) => f.aggregatable) ?? [];
  const filterableFields = source?.fields.filter((f) => f.filterable) ?? [];

  function resetForSource(key: string): void {
    setSourceKey(key);
    setMetrics([{ aggregate: 'count' }]);
    setGroupBy([]);
    setFilters([]);
    setChartKind('table');
    setDrill(null);
    setActiveReport(null);
  }

  const wireFilters = source ? toWireFilters(source, filters) : [];
  const queryBody = useMemo(
    () =>
      source === undefined
        ? null
        : {
            source: source.key,
            metrics: metrics.filter((m) => m.aggregate === 'count' || m.field !== undefined),
            ...(groupBy.length > 0 ? { groupBy } : {}),
            ...(wireFilters.length > 0 ? { filters: wireFilters } : {}),
            limit: 100,
          },
    // Deps bewusst als JSON-Strings — stabile Referenzen für den Query-Key
    [sourceKey, JSON.stringify(metrics), JSON.stringify(groupBy), JSON.stringify(wireFilters)],
  );

  const result = useQuery({
    queryKey: ['analytics', 'query', queryBody],
    queryFn: () => api<QueryResult>('/api/v1/analytics/query', { method: 'POST', body: queryBody }),
    enabled: queryBody !== null && queryBody.metrics.length > 0,
  });

  const drillRows = useQuery({
    queryKey: ['analytics', 'rows', sourceKey, drill],
    queryFn: () =>
      api<QueryResult>('/api/v1/analytics/rows', {
        method: 'POST',
        body: { source: sourceKey, filters: drill, limit: 100 },
      }),
    enabled: drill !== null,
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      api<{ report: SavedReport }>('/api/v1/analytics/reports', {
        method: 'POST',
        body: {
          name: saveName,
          shared: saveShared,
          config: { source: sourceKey, metrics, groupBy, filters, chartKind },
        },
      }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['analytics', 'saved'] });
      setSaveOpen(false);
      setActiveReport(data.report.id);
      toast.success('Bericht gespeichert.');
    },
    onError: (error) => toast.error(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/api/v1/analytics/reports/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['analytics', 'saved'] });
      toast.success('Bericht gelöscht.');
    },
    onError: (error) => toast.error(error.message),
  });

  function applyReport(report: SavedReport): void {
    const config = report.config as {
      source?: string;
      metrics?: MetricDef[];
      groupBy?: GroupByDef[];
      filters?: FilterDraft[];
      chartKind?: ChartKind;
    };
    if (config.source === undefined) return;
    setSourceKey(config.source);
    setMetrics(config.metrics ?? [{ aggregate: 'count' }]);
    setGroupBy(config.groupBy ?? []);
    setFilters(config.filters ?? []);
    setChartKind(config.chartKind ?? 'table');
    setDrill(null);
    setActiveReport(report.id);
    setSaveName(report.name);
  }

  async function downloadExport(format: 'csv' | 'xlsx'): Promise<void> {
    if (queryBody === null) return;
    const tenant = tenantStore.get();
    const response = await fetch('/api/v1/analytics/export', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
        ...(tenant !== null ? { 'x-tenant': tenant } : {}),
      },
      body: JSON.stringify({
        query: queryBody,
        format,
        title: saveName !== '' ? saveName : (source?.label ?? 'Auswertung'),
      }),
    });
    if (!response.ok) {
      toast.error('Export fehlgeschlagen.');
      return;
    }
    const disposition = response.headers.get('content-disposition') ?? '';
    const match = /filename="([^"]+)"/.exec(disposition);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = match?.[1] ?? `auswertung.${format}`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  /** Klick auf eine Aggregat-Zeile → Filter für die Datensätze dahinter. */
  function drillInto(row: Record<string, unknown>): void {
    if (result.data === undefined || source === undefined) return;
    const extra: Record<string, unknown>[] = [];
    for (const column of result.data.columns) {
      if (!column.dimension || column.sourceField === undefined) continue;
      const value = row[column.key];
      if (value === null || value === undefined || value === '') {
        extra.push({ field: column.sourceField, op: 'empty' });
      } else if (column.bucket !== undefined) {
        const [from, to] = bucketRange(cellText(value), column.bucket);
        extra.push({ field: column.sourceField, op: 'between', values: [from, to] });
      } else {
        extra.push({ field: column.sourceField, op: 'eq', value });
      }
    }
    setDrill([...wireFilters, ...extra]);
  }

  const canLines =
    result.data !== undefined &&
    result.data.columns.some((c) => c.dimension && c.bucket !== undefined);

  const savedItems = saved.data?.items ?? [];

  return (
    <div className="page page--wide">
      <div className="anl-head">
        <div>
          <h1 className="page__title">Auswertungen</h1>
          <p className="page__lead">
            Eigene Berichte bauen: Quelle wählen, Kennzahlen und Filter festlegen — Klick auf eine
            Zeile zeigt die Datensätze dahinter.
          </p>
        </div>
        <div className="anl-head__actions">
          <Button
            variant="subtle"
            disabled={result.data === undefined}
            onClick={() => void downloadExport('csv')}
          >
            <Download size={14} aria-hidden /> CSV
          </Button>
          <Button
            variant="subtle"
            disabled={result.data === undefined}
            onClick={() => void downloadExport('xlsx')}
          >
            <Download size={14} aria-hidden /> Excel
          </Button>
          <Button disabled={source === undefined} onClick={() => setSaveOpen(true)}>
            <Save size={14} aria-hidden /> Speichern
          </Button>
        </div>
      </div>

      {savedItems.length > 0 ? (
        <div className="anl-saved" role="list" aria-label="Gespeicherte Berichte">
          {savedItems.map((report) => (
            <span
              key={report.id}
              className={`anl-saved__chip${activeReport === report.id ? ' anl-saved__chip--active' : ''}`}
              role="listitem"
            >
              <button type="button" onClick={() => applyReport(report)}>
                {report.shared ? <Share2 size={12} aria-label="Geteilt" /> : null}
                {report.name}
              </button>
              {report.own ? (
                <button
                  type="button"
                  aria-label={`Bericht ${report.name} löschen`}
                  onClick={() => {
                    void confirm({
                      title: 'Bericht löschen?',
                      description: `„${report.name}" wird endgültig gelöscht.`,
                      confirmLabel: 'Löschen',
                      danger: true,
                    }).then((ok) => {
                      if (ok) deleteMutation.mutate(report.id);
                    });
                  }}
                >
                  <X size={12} aria-hidden />
                </button>
              ) : null}
            </span>
          ))}
        </div>
      ) : null}

      <div className="anl-layout">
        <Card className="anl-config">
          <Field label="Datenquelle">
            {(id) => (
              <Select
                id={id}
                value={sourceKey}
                onChange={(event) => resetForSource(event.target.value)}
              >
                <option value="">Quelle wählen …</option>
                {[...new Set(sources.data?.sources.map((s) => s.moduleLabel))].map((module) => (
                  <optgroup key={module} label={module}>
                    {sources.data?.sources
                      .filter((s) => s.moduleLabel === module)
                      .map((s) => (
                        <option key={s.key} value={s.key}>
                          {s.label}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </Select>
            )}
          </Field>

          {source ? (
            <>
              <div className="anl-config__section">Kennzahlen</div>
              {metrics.map((metric, index) => (
                <div key={index} className="anl-config__row">
                  <Select
                    aria-label={`Aggregat ${index + 1}`}
                    value={metric.aggregate}
                    onChange={(event) => {
                      const aggregate = event.target.value as Aggregate;
                      setMetrics((old) =>
                        old.map((m, i) => {
                          if (i !== index) return m;
                          if (aggregate === 'count') return { aggregate };
                          const field = m.field ?? aggregatableFields[0]?.key;
                          return { aggregate, ...(field !== undefined ? { field } : {}) };
                        }),
                      );
                    }}
                  >
                    {(Object.keys(AGGREGATE_LABELS) as Aggregate[]).map((agg) => (
                      <option key={agg} value={agg}>
                        {AGGREGATE_LABELS[agg]}
                      </option>
                    ))}
                  </Select>
                  {metric.aggregate !== 'count' ? (
                    <Select
                      aria-label={`Kennzahl-Feld ${index + 1}`}
                      value={metric.field ?? ''}
                      onChange={(event) =>
                        setMetrics((old) =>
                          old.map((m, i) =>
                            i === index ? { ...m, field: event.target.value } : m,
                          ),
                        )
                      }
                    >
                      {aggregatableFields.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <span className="anl-config__hint">aller Datensätze</span>
                  )}
                  {metrics.length > 1 ? (
                    <button
                      type="button"
                      className="anl-config__remove"
                      aria-label="Kennzahl entfernen"
                      onClick={() => setMetrics((old) => old.filter((_, i) => i !== index))}
                    >
                      <X size={14} aria-hidden />
                    </button>
                  ) : null}
                </div>
              ))}
              {metrics.length < 4 ? (
                <button
                  type="button"
                  className="anl-config__add"
                  onClick={() =>
                    setMetrics((old) => [
                      ...old,
                      aggregatableFields.length > 0
                        ? { aggregate: 'sum', field: aggregatableFields[0]!.key }
                        : { aggregate: 'count' },
                    ])
                  }
                >
                  <Plus size={13} aria-hidden /> Kennzahl
                </button>
              ) : null}

              <div className="anl-config__section">Gruppierung</div>
              {groupBy.map((group, index) => {
                const field = source.fields.find((f) => f.key === group.field);
                return (
                  <div key={index} className="anl-config__row">
                    <Select
                      aria-label={`Gruppierung ${index + 1}`}
                      value={group.field}
                      onChange={(event) =>
                        setGroupBy((old) =>
                          old.map((g, i) => (i === index ? { field: event.target.value } : g)),
                        )
                      }
                    >
                      {groupableFields.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.label}
                        </option>
                      ))}
                    </Select>
                    {field?.type === 'date' ? (
                      <Select
                        aria-label="Zeitraster"
                        value={group.bucket ?? 'month'}
                        onChange={(event) =>
                          setGroupBy((old) =>
                            old.map((g, i) =>
                              i === index ? { ...g, bucket: event.target.value as Bucket } : g,
                            ),
                          )
                        }
                      >
                        {(Object.keys(BUCKET_LABELS) as Bucket[]).map((b) => (
                          <option key={b} value={b}>
                            je {BUCKET_LABELS[b]}
                          </option>
                        ))}
                      </Select>
                    ) : null}
                    <button
                      type="button"
                      className="anl-config__remove"
                      aria-label="Gruppierung entfernen"
                      onClick={() => setGroupBy((old) => old.filter((_, i) => i !== index))}
                    >
                      <X size={14} aria-hidden />
                    </button>
                  </div>
                );
              })}
              {groupBy.length < 2 && groupableFields.length > 0 ? (
                <button
                  type="button"
                  className="anl-config__add"
                  onClick={() => setGroupBy((old) => [...old, { field: groupableFields[0]!.key }])}
                >
                  <Plus size={13} aria-hidden /> Gruppierung
                </button>
              ) : null}

              <div className="anl-config__section">Filter</div>
              {filters.map((filter, index) => {
                const field = source.fields.find((f) => f.key === filter.field);
                const ops = field ? opsForType(field.type, field.options !== undefined) : [];
                const needsValue = filter.op !== 'empty' && filter.op !== 'not_empty';
                const inputType =
                  field?.type === 'date'
                    ? 'date'
                    : field?.type === 'money' || field?.type === 'number'
                      ? 'number'
                      : 'text';
                return (
                  <div key={index} className="anl-config__filter">
                    <div className="anl-config__row">
                      <Select
                        aria-label={`Filterfeld ${index + 1}`}
                        value={filter.field}
                        onChange={(event) => {
                          const next = source.fields.find((f) => f.key === event.target.value);
                          setFilters((old) =>
                            old.map((f, i) =>
                              i === index
                                ? {
                                    field: event.target.value,
                                    op: next
                                      ? opsForType(next.type, next.options !== undefined)[0]!
                                      : 'eq',
                                    value: '',
                                    value2: '',
                                  }
                                : f,
                            ),
                          );
                        }}
                      >
                        {filterableFields.map((f) => (
                          <option key={f.key} value={f.key}>
                            {f.label}
                          </option>
                        ))}
                      </Select>
                      <Select
                        aria-label={`Operator ${index + 1}`}
                        value={filter.op}
                        onChange={(event) =>
                          setFilters((old) =>
                            old.map((f, i) =>
                              i === index ? { ...f, op: event.target.value as FilterOp } : f,
                            ),
                          )
                        }
                      >
                        {ops.map((op) => (
                          <option key={op} value={op}>
                            {OP_LABELS[op]}
                          </option>
                        ))}
                      </Select>
                      <button
                        type="button"
                        className="anl-config__remove"
                        aria-label="Filter entfernen"
                        onClick={() => setFilters((old) => old.filter((_, i) => i !== index))}
                      >
                        <X size={14} aria-hidden />
                      </button>
                    </div>
                    {needsValue ? (
                      <div className="anl-config__row">
                        {field?.options !== undefined ? (
                          <Select
                            aria-label={`Filterwert ${index + 1}`}
                            value={filter.value}
                            onChange={(event) =>
                              setFilters((old) =>
                                old.map((f, i) =>
                                  i === index ? { ...f, value: event.target.value } : f,
                                ),
                              )
                            }
                          >
                            <option value="">wählen …</option>
                            {field.options.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Select>
                        ) : field?.type === 'boolean' ? (
                          <Select
                            aria-label={`Filterwert ${index + 1}`}
                            value={filter.value}
                            onChange={(event) =>
                              setFilters((old) =>
                                old.map((f, i) =>
                                  i === index ? { ...f, value: event.target.value } : f,
                                ),
                              )
                            }
                          >
                            <option value="">wählen …</option>
                            <option value="true">ja</option>
                            <option value="false">nein</option>
                          </Select>
                        ) : (
                          <Input
                            aria-label={`Filterwert ${index + 1}`}
                            type={inputType}
                            step={field?.type === 'money' ? '0.01' : undefined}
                            placeholder={field?.type === 'money' ? '€' : undefined}
                            value={filter.value}
                            onChange={(event) =>
                              setFilters((old) =>
                                old.map((f, i) =>
                                  i === index ? { ...f, value: event.target.value } : f,
                                ),
                              )
                            }
                          />
                        )}
                        {filter.op === 'between' ? (
                          <Input
                            aria-label={`Filterwert bis ${index + 1}`}
                            type={inputType}
                            step={field?.type === 'money' ? '0.01' : undefined}
                            value={filter.value2}
                            onChange={(event) =>
                              setFilters((old) =>
                                old.map((f, i) =>
                                  i === index ? { ...f, value2: event.target.value } : f,
                                ),
                              )
                            }
                          />
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
              {filterableFields.length > 0 ? (
                <button
                  type="button"
                  className="anl-config__add"
                  onClick={() => {
                    const first = filterableFields[0]!;
                    setFilters((old) => [
                      ...old,
                      {
                        field: first.key,
                        op: opsForType(first.type, first.options !== undefined)[0]!,
                        value: '',
                        value2: '',
                      },
                    ]);
                  }}
                >
                  <Plus size={13} aria-hidden /> Filter
                </button>
              ) : null}

              <div className="anl-config__section">Darstellung</div>
              <div className="anl-config__row">
                <Select
                  aria-label="Diagramm-Typ"
                  value={chartKind}
                  onChange={(event) => setChartKind(event.target.value as ChartKind)}
                >
                  <option value="table">Tabelle</option>
                  <option value="bars" disabled={groupBy.length === 0}>
                    Balken
                  </option>
                  <option value="lines" disabled={!canLines}>
                    Linien (Zeitverlauf)
                  </option>
                </Select>
              </div>
            </>
          ) : null}
        </Card>

        <div className="anl-preview">
          {source === undefined ? (
            <Card>
              <EmptyState
                icon={<PieChart size={28} aria-hidden />}
                title="Quelle wählen"
                description="Links eine Datenquelle wählen — z. B. Rechnungen, Ausgaben, Deals oder Zeiten."
              />
            </Card>
          ) : result.isLoading ? (
            <Card>
              <SkeletonText lines={8} />
            </Card>
          ) : result.isError ? (
            <Card>
              <EmptyState
                icon={<PieChart size={28} aria-hidden />}
                title="Abfrage fehlgeschlagen"
                description={result.error.message}
              />
            </Card>
          ) : result.data !== undefined ? (
            <Card>
              {result.data.rows.length === 0 ? (
                <EmptyState
                  icon={<PieChart size={28} aria-hidden />}
                  title="Keine Daten"
                  description="Für diese Kombination aus Filtern und Zeitraum gibt es keine Datensätze."
                />
              ) : (
                <>
                  {chartKind === 'bars' ? (
                    <BarsPreview result={result.data} source={source} />
                  ) : null}
                  {chartKind === 'lines' ? (
                    <LinesPreview result={result.data} source={source} />
                  ) : null}
                  {chartKind === 'table' ? (
                    <div className="anl-table-wrap">
                      <table className="au-table">
                        <thead>
                          <tr>
                            {result.data.columns.map((column) => (
                              <th
                                key={column.key}
                                className={column.dimension ? undefined : 'anl-num'}
                              >
                                {column.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {result.data.rows.map((row, index) => (
                            <tr
                              key={index}
                              className={groupBy.length > 0 ? 'anl-row--clickable' : undefined}
                              onClick={groupBy.length > 0 ? () => drillInto(row) : undefined}
                              title={groupBy.length > 0 ? 'Datensätze anzeigen' : undefined}
                            >
                              {result.data.columns.map((column) => (
                                <td
                                  key={column.key}
                                  className={column.dimension ? undefined : 'anl-num'}
                                >
                                  {formatCell(column, row[column.key], source)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                  <div className="anl-preview__foot">
                    {result.data.truncated
                      ? 'Ergebnis gekürzt (Limit erreicht) — Filter enger fassen.'
                      : `${result.data.rows.length} ${groupBy.length > 0 ? 'Gruppen' : 'Zeile(n)'}`}
                    {groupBy.length > 0 ? ' · Zeile anklicken für Datensätze' : ''}
                  </div>
                </>
              )}
            </Card>
          ) : null}
        </div>
      </div>

      <Dialog
        open={drill !== null}
        onClose={() => setDrill(null)}
        wide
        aria-label="Datensätze hinter der Auswahl"
      >
        <div className="anl-drill">
          <div className="anl-drill__head">
            <h2>Datensätze</h2>
            <Button variant="ghost" onClick={() => setDrill(null)} aria-label="Schließen">
              <X size={16} aria-hidden />
            </Button>
          </div>
          {drillRows.isLoading ? (
            <SkeletonText lines={6} />
          ) : drillRows.data !== undefined ? (
            <div className="anl-table-wrap">
              <table className="au-table">
                <thead>
                  <tr>
                    {drillRows.data.columns.map((column) => (
                      <th key={column.key}>{column.label}</th>
                    ))}
                    {source?.detail !== undefined ? <th /> : null}
                  </tr>
                </thead>
                <tbody>
                  {drillRows.data.rows.map((row, index) => (
                    <tr key={index}>
                      {drillRows.data.columns.map((column) => (
                        <td key={column.key}>{formatCell(column, row[column.key], source)}</td>
                      ))}
                      {source?.detail !== undefined ? (
                        <td>
                          {typeof row['_id'] === 'string' ? (
                            <Link
                              to={source.detail.route.replace(':id', row['_id'])}
                              onClick={() => setDrill(null)}
                            >
                              Öffnen
                            </Link>
                          ) : null}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
              {drillRows.data.truncated ? (
                <div className="anl-preview__foot">Nur die ersten 100 Datensätze angezeigt.</div>
              ) : null}
            </div>
          ) : null}
        </div>
      </Dialog>

      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} aria-label="Bericht speichern">
        <form
          className="anl-save"
          onSubmit={(event) => {
            event.preventDefault();
            if (saveName.trim() === '') return;
            saveMutation.mutate();
          }}
        >
          <h2>Bericht speichern</h2>
          <Field label="Name">
            {(id) => (
              <Input
                id={id}
                autoFocus
                value={saveName}
                onChange={(event) => setSaveName(event.target.value)}
                placeholder="z. B. Umsatz je Kunde (Quartal)"
              />
            )}
          </Field>
          <div className="anl-save__shared">
            <Switch
              checked={saveShared}
              onChange={setSaveShared}
              aria-label="Mit dem Team teilen"
            />
            <span>Mit dem Team teilen</span>
          </div>
          <div className="anl-save__actions">
            <Button type="button" variant="subtle" onClick={() => setSaveOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={saveName.trim() === '' || saveMutation.isPending}>
              Speichern
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
